import React, { useCallback, useState, useRef, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const DropzoneContainer = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'border-color 0.3s',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

interface FileUploadProps {
  onFilesChange: (files: File[]) => void;
  onFileSelect?: (file: File) => void;
  selectedFile?: File | null;
}

interface TreeNode {
  id: string;
  name: string;
  file?: File;
  children?: TreeNode[];
}

const buildFileTree = (files: File[]): TreeNode[] => {
  const tree: TreeNode[] = [];
  const map: { [key: string]: TreeNode } = {};

  files.forEach(file => {
    const path = (file as any).webkitRelativePath || file.name;
    const parts = path.split('/');
    
    parts.reduce((acc: string, part: string, index: number) => {
      const isLeaf = index === parts.length - 1;
      const currentPath = acc ? `${acc}/${part}` : part;
      
      if (!map[currentPath]) {
        map[currentPath] = {
          id: currentPath,
          name: part,
          children: isLeaf ? undefined : [],
          file: isLeaf ? file : undefined,
        };

        if (acc && map[acc]) {
          map[acc].children!.push(map[currentPath]);
        } else if (!acc) {
          tree.push(map[currentPath]);
        }
      }
      
      return currentPath;
    }, '');
  });

  return tree;
};

const FileUpload: React.FC<FileUploadProps> = ({ onFilesChange, onFileSelect, selectedFile }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const directoryInputRef = useRef<HTMLInputElement>(null);

  const handleToggle = (nodeId: string) => {
    setExpandedNodes((prevExpanded) =>
      prevExpanded.includes(nodeId)
        ? prevExpanded.filter((id) => id !== nodeId)
        : [...prevExpanded, nodeId]
    );
  };

  const handleFileChange = (newFiles: File[]) => {
    const updatedFiles = [...files, ...newFiles].reduce((acc, file) => {
        const path = (file as any).webkitRelativePath || file.name;
        if (!acc.find(f => ((f as any).webkitRelativePath || f.name) === path)) {
            acc.push(file);
        }
        return acc;
    }, [] as File[]);
    
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const onDrop = useCallback(async (acceptedFiles: File[], fileRejections: any, event: any) => {
    const items = event.dataTransfer.items;
    let newFiles: File[] = [];

    // Función recursiva para procesar directorios
    const processDirectory = async (directoryHandle: any, path: string) => {
      const files: File[] = [];
      for await (const entry of directoryHandle.values()) {
        const newPath = path ? `${path}/${entry.name}` : entry.name;
        if (entry.kind === 'file') {
          const file = await entry.getFile();
          Object.defineProperty(file, 'webkitRelativePath', {
            value: newPath,
            writable: true,
          });
          files.push(file);
        } else if (entry.kind === 'directory') {
          files.push(...await processDirectory(entry, newPath));
        }
      }
      return files;
    };

    if (items && items.length > 0 && typeof items[0].getAsFileSystemHandle === 'function') {
        // Usar la API moderna de File System Access
        const handlePromises = Array.from(items).map((item: any) => item.getAsFileSystemHandle());
        for (const handle of await Promise.all(handlePromises)) {
            if (handle.kind === 'file') {
                const file = await handle.getFile();
                 Object.defineProperty(file, 'webkitRelativePath', {
                    value: file.name,
                    writable: true,
                });
                newFiles.push(file);
            } else if (handle.kind === 'directory') {
                newFiles.push(...await processDirectory(handle, handle.name));
            }
        }
    } else {
        // Fallback para navegadores más antiguos usando webkitGetAsEntry
        const processEntry = async (entry: any): Promise<File[]> => {
            if (entry.isFile) {
                return new Promise<File[]>((resolve) => {
                    entry.file((file: File) => {
                         Object.defineProperty(file, 'webkitRelativePath', {
                            value: entry.fullPath.startsWith('/') ? entry.fullPath.substring(1) : entry.fullPath,
                            writable: true,
                        });
                        resolve([file]);
                    }, () => resolve([]));
                });
            } else if (entry.isDirectory) {
                const reader = entry.createReader();
                return new Promise<File[]>((resolve) => {
                    reader.readEntries(async (entries: any[]) => {
                        const files = await Promise.all(entries.map(processEntry));
                        resolve(files.flat());
                    });
                });
            }
            return [];
        };

        const entryPromises = Array.from(items).map((item: any) => {
            const entry = item.webkitGetAsEntry();
            if (entry) {
                return processEntry(entry);
            }
            return Promise.resolve([]);
        });
        
        newFiles = (await Promise.all(entryPromises)).flat();
    }

    handleFileChange(newFiles);
  }, [files, onFilesChange]);

  const removeFile = (nodeToRemove: TreeNode) => {
    const newFiles = files.filter(file => {
        const path = (file as any).webkitRelativePath || file.name;
        return !path.startsWith(nodeToRemove.id);
    });
    setFiles(newFiles);
    onFilesChange(newFiles);
  };

  const handleDirectoryUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      handleFileChange(Array.from(event.target.files));
    }
    if(event.target) {
        event.target.value = '';
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, noClick: true });

  const fileTree = useMemo(() => buildFileTree(files), [files]);

  const renderTree = (nodes: TreeNode[]) => {
    return (
      <ul className="file-tree">
        {nodes.map((node) => {
          const isExpanded = expandedNodes.includes(node.id);
          return (
            <li key={node.id}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 0.5,
                  cursor: 'pointer',
                  backgroundColor: selectedFile && node.file && selectedFile.name === node.file.name && selectedFile.size === node.file.size ? '#f0f0f0' : 'transparent',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
                onClick={() => {
                  if (node.children) {
                    handleToggle(node.id);
                  } else if (onFileSelect && node.file) {
                    onFileSelect(node.file);
                  }
                }}
              >
                <Box sx={{ width: 40, textAlign: 'center' }}>
                  {node.children && (isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />)}
                </Box>
                <span>{node.children ? '📁' : '📄'}</span>
                <Typography sx={{ flexGrow: 1, ml: 1 }}>{node.name}</Typography>
                {!node.children && <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>{`${(node.file!.size / 1024).toFixed(2)} KB`}</Typography>}
                <IconButton size="small" edge="end" aria-label="delete" onClick={(e) => { e.stopPropagation(); removeFile(node); }}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {isExpanded && Array.isArray(node.children) && node.children.length > 0 && renderTree(node.children)}
            </li>
          );
        })}
      </ul>
    );
  };

  const treeStyles = `
    .file-tree, .file-tree ul {
      list-style: none;
      padding-left: 22px;
    }
    .file-tree li {
      position: relative;
    }
    .file-tree li::before {
      content: "";
      position: absolute;
      left: -15px;
      top: 0;
      width: 1px;
      height: 100%;
      background-color: #ccc;
    }
    .file-tree li::after {
      content: "";
      position: absolute;
      left: -15px;
      top: 18px;
      width: 15px;
      height: 1px;
      background-color: #ccc;
    }
    .file-tree li:last-child::before {
      height: 18px;
    }
  `;

  return (
    <>
      <style>{treeStyles}</style>
      <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
        <Box sx={{ flex: '1 1 50%', minHeight: 0 }}>
          <DropzoneContainer {...getRootProps()} sx={{height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography>
              {isDragActive ? 'Suelta los archivos o carpetas aquí...' : 'Arrastra y suelta archivos o carpetas aquí'}
            </Typography>
            <Button
                variant="outlined"
                startIcon={<FolderOpenIcon />}
                onClick={() => directoryInputRef.current?.click()}
                sx={{ mt: 2 }}
            >
                Seleccionar Carpeta
            </Button>
          </DropzoneContainer>
          <input
            type="file"
            ref={directoryInputRef}
            onChange={handleDirectoryUpload}
            style={{ display: 'none' }}
            {...({ webkitdirectory: "true", directory: "true" } as any)}
          />
        </Box>
        <Box sx={{ flex: '1 1 50%', border: fileTree.length > 0 ? '1px solid #ddd' : 'none', borderRadius: 1, p: fileTree.length > 0 ? 1 : 0, minHeight: 0, overflowY: 'auto' }}>
          {fileTree.length > 0 ? (
              renderTree(fileTree)
          ) : (
              <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'text.secondary'}}>
                  <Typography>Vista previa de la jerarquía de carpetas</Typography>
              </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default FileUpload;